// Shared, client-safe definitions for the vendor partner portal and the
// internal review inbox. Keys are stable: they are persisted in
// vendor_partner_leads.onboarding as { [key]: ISO timestamp }.

export type OnboardingStepKey =
  | "reviewed"
  | "fitment_call"
  | "nda_signed"
  | "msa_signed"
  | "tools_access"
  | "briefing_shared"
  | "first_submission"
  | "calibrated";

export type OnboardingStep = {
  key: OnboardingStepKey;
  label: string;
  detail: string;
};

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    key: "reviewed",
    label: "Application reviewed",
    detail: "Our sourcing team has read your submission and the Foyer fitment read.",
  },
  {
    key: "fitment_call",
    label: "Fitment call held",
    detail: "A 30 minute call to confirm bench, employment model, and coverage.",
  },
  {
    key: "nda_signed",
    label: "NDA signed",
    detail: "Mutual NDA in place before any engagement context is shared.",
  },
  {
    key: "msa_signed",
    label: "MSA signed",
    detail: "Master agreement executed, including the right-to-hire clause.",
  },
  {
    key: "tools_access",
    label: "Tools and time logging set up",
    detail: "Your people are set up on our tools and log time on the agreed cadence.",
  },
  {
    key: "briefing_shared",
    label: "First briefing shared",
    detail: "You receive a written brief with the skill, level, and coverage we need.",
  },
  {
    key: "first_submission",
    label: "First profiles submitted",
    detail: "Profiles submitted in the agreed format, screened by Fynn.",
  },
  {
    key: "calibrated",
    label: "Calibrated",
    detail: "We have calibrated on quality and turnaround, and you are in the rotation.",
  },
];

export type PartnerResource = {
  label: string;
  detail: string;
  href: string;
  kind: "document" | "field note";
};

export const PARTNER_RESOURCES: PartnerResource[] = [
  {
    label: "Vendor Partner Agency Do's and Don'ts",
    detail: "What we expect from an agency: employment model, communication, replacements, invoicing.",
    href: "/docs/vendor-partner-agency-dos-and-donts.pdf",
    kind: "document",
  },
  {
    label: "Vendor Partner Candidate Do's and Don'ts",
    detail: "Brief your people with this before any screening call or submission.",
    href: "/docs/vendor-partner-candidate-dos-and-donts.pdf",
    kind: "document",
  },
  {
    label: "Trust is earned, not assumed",
    detail: "How we think about accountability inside a shared delivery system.",
    href: "/blog/trust-is-earned-not-assumed",
    kind: "field note",
  },
  {
    label: "What we learned running 5 agent pods",
    detail: "How agentic delivery changes the work your people will be part of.",
    href: "/blog/what-we-learned-running-5-agent-pods",
    kind: "field note",
  },
  {
    label: "The delivery intelligence gap",
    detail: "The evidence trail we keep on every engagement, and why it matters to you.",
    href: "/blog/the-delivery-intelligence-gap",
    kind: "field note",
  },
  {
    label: "How we work",
    detail: "The four lenses, the accountable team model, and where partners plug in.",
    href: "/how-we-do",
    kind: "field note",
  },
];

export const STAGE_LABEL: Record<string, string> = {
  new: "New",
  nda_sent: "NDA sent",
  msa_signed: "MSA signed",
  first_submission: "First submission",
  calibrated: "Calibrated",
  preferred: "Preferred",
  declined: "Not moving ahead",
};

export function parseOnboarding(value: unknown): Partial<Record<OnboardingStepKey, string>> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const out: Partial<Record<OnboardingStepKey, string>> = {};
  for (const step of ONBOARDING_STEPS) {
    const v = (value as Record<string, unknown>)[step.key];
    if (typeof v === "string" && v) out[step.key] = v;
  }
  return out;
}
